import { Component, ElementRef, Input, Renderer2, OnInit } from '@angular/core';
import { isNotNullOrUndefined, System } from 'ngx-core-services';
import { BehaviorSubject, combineLatest, Observable, Subject, of } from 'rxjs';
import { startWith, skip, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { EChartsOption } from 'echarts';
import { format } from 'date-fns';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import {
  requestSelectedHeatpumpDailyTelemetryData,
  selectedSystemTz,
  selectedSystemUid,
  SystemsState
} from '../../store';
import { selectHeatpumpDailyTelemetryData } from '../../store/selectors/heatpump.selectors';
import { appBuildHeatPumpDailyGraph } from '../utils/heat-pump-chart.opts';

export type Unit = '°C' | 'W' | 'Kwh';
export type AttributeId = 'OUT_TEMP' | 'RET_TEMP' | 'DHW_TEMP' | 'HP_E_PWR' | 'HP_E_EG' | 'HP_E_ET' | 'HP_T_PWR' | 'HP_T_EG' | 'HP_T_ET' | 'ENV_TEMP';
export interface AttributeOption { id: AttributeId; unit: Unit; }
export interface AttributeCategory { categoryId: 'temperatures' | 'powers' | 'energies'; attributes: AttributeOption[]; }

export const DEFAULT_ATTR_IDS: AttributeId[] = ['OUT_TEMP', 'RET_TEMP'];

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-750': 750,
})
@Component({
  selector: 'lib-heatpump-daily-telemetry',
  templateUrl: './heatpump-daily-telemetry.component.html',
  styleUrls: ['./heatpump-daily-telemetry.component.scss'],
})
export class HeatpumpDailyTelemetryComponent implements ICustomBlock, IReduxBlock, OnInit {
  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly _deviceId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set deviceUid(value: string | undefined) {
    this._deviceId$.next(value);
  }

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;


  private pickOptionsByIds(ids: AttributeId[]): AttributeOption[] {
    const map = new Map<AttributeId, AttributeOption>();
    for (const cat of this.attributes) {
      for (const opt of cat.attributes) map.set(opt.id, opt);
    }
    return ids.map(id => map.get(id)).filter((v): v is AttributeOption => !!v);
  }

  readonly selectedAttributes$ = new BehaviorSubject<AttributeOption[]>([]);

  readonly canDeselectAttributes$: Observable<boolean> = this.selectedAttributes$.pipe(
    map(attrs => attrs.length > 1),
    startWith(false),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly _labels = {
    title: 'Heatpump Daily Telemetry',
    home: 'Home',
    grid: 'Grid',
    //
    temperatures: 'Temperature',
    powers: 'Potenze',
    energies: 'Energie',
    noDataForTheSelectedPeriod: 'No data available for the selected period',

    OUT_TEMP: 'Temperatura di mandata ',
    RET_TEMP: 'Temperatura di ritorno ',
    DHW_TEMP: 'Temperatura del boiler di ACS',
    HP_E_PWR: 'Potenza assorbita dalla macchina',
    HP_E_EG: 'Energia assorbita giornaliera',
    HP_E_ET: 'Energia assorbita totale',
    HP_T_PWR: 'Potenza termica equivalente',
    HP_T_EG: 'Energia termica equivalente giornaliera',
    HP_T_ET: 'Energia termica equivalente totale',
    ENV_TEMP: 'Temperatura ambiente',
    youCanAddPropertiesForUpToTwoUnitsOfMeasurement: 'Puoi aggiungere proprietà per massimo due unità di misura',
    clear: 'Pulisci',
    updateChart: 'Aggiorna grafico',
    createChart: 'Conferma selezione',
    deleteChart: 'Elimina grafico',

    Standby: 'Standby',
    Running: 'Running',
    Starting: 'Starting',
    Shutdown: 'Shutdown',
    Alarm: 'Alarm',
    Cooling: "Cooling",
    Heating: "Heating",
    HotWater: "Hot Water",
    modeTitle: 'Mode',
  };

  readonly labels$ = new BehaviorSubject<typeof this._labels>(this._labels);
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    this.labels$.next(this._labels);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  attributes: AttributeCategory[] = [{
    categoryId: 'temperatures',
    attributes: [
      { id: 'OUT_TEMP', unit: '°C' },
      { id: 'RET_TEMP', unit: '°C' },
      { id: 'DHW_TEMP', unit: '°C' },
      { id: 'ENV_TEMP', unit: '°C' },
    ],
  },
  {
    categoryId: 'powers',
    attributes: [
      { id: 'HP_E_PWR', unit: 'W' },
      { id: 'HP_T_PWR', unit: 'W' },
    ],
  },
  {
    categoryId: 'energies',
    attributes: [
      { id: 'HP_E_EG', unit: 'Kwh' },
      { id: 'HP_E_ET', unit: 'Kwh' },
      { id: 'HP_T_EG', unit: 'Kwh' },
      { id: 'HP_T_ET', unit: 'Kwh' },
    ],
  }];

  compareFn = (a?: AttributeOption, b?: AttributeOption) => a?.id === b?.id;

  onAttributesValueChange(values: AttributeOption[]): void {
    this.selectedAttributes$.next(values || []);
  }

  readonly dateSelection$ = new BehaviorSubject<string>(format(new Date(), 'yyyy-MM-dd'));

  readonly requestHeatPumpDailyTelemetry$ = combineLatest([
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this._deviceId$.pipe(isNotNullOrUndefined()),
    this.dateSelection$,
    this._refresh$,
    this._instanceId$,
  ]).pipe(
    tap(([systemId, deviceId, date, forceRefresh, instanceId]) => {
      this.store.dispatch(requestSelectedHeatpumpDailyTelemetryData({ systemId, instanceId, deviceId, forceRefresh, date }));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly selectHeatpumpDailyTelemetryData$ = combineLatest([
    this._instanceId$,
    this._deviceId$.pipe(isNotNullOrUndefined()),
    this.dateSelection$,
  ]).pipe(
    switchMap(([instanceId, deviceId]) =>
      this.store
        .select(selectHeatpumpDailyTelemetryData(instanceId, deviceId))
        .pipe(isNotNullOrUndefined())
    ),
    isNotNullOrUndefined()
  );

  readonly hasData$: Observable<boolean> = this.selectHeatpumpDailyTelemetryData$.pipe(
    map(rows => (rows as any[]).length > 0),
    startWith(false),
  );

  readonly heatPumpChartOption$: Observable<EChartsOption> = combineLatest([
    this.selectHeatpumpDailyTelemetryData$,
    this.labels$,
    this.dateSelection$,
    this.selectedAttributes$.asObservable().pipe(startWith(this.selectedAttributes$.value)),
    this.store.select(selectedSystemTz).pipe(isNotNullOrUndefined()),
  ]).pipe(
    switchMap(([rows, labels, date, selectedValues, tz]) => {
      const chartAttributes = selectedValues.map(s => ({ id: s.id, attribute: { graphOpts: { unit: s.unit, noArea: true } } }));
      const selectedIds = selectedValues.map(s => s.id);
      const option = appBuildHeatPumpDailyGraph(rows as any, date, String(tz), chartAttributes as any, labels, selectedIds as any);
      return of(option);
    })
  );

  constructor(
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
  ) { }

  ngOnInit(): void {
    const saved = localStorage.getItem('heatpump-selected-attrs');
    const ids: AttributeId[] = saved ? JSON.parse(saved) : DEFAULT_ATTR_IDS;

    const opts = this.pickOptionsByIds(ids);
    this.selectedAttributes$.next(opts);

    this.selectedAttributes$.pipe(skip(1)).subscribe(values => {
      const idsToSave = values.map(v => v.id);
      localStorage.setItem('heatpump-selected-attrs', JSON.stringify(idsToSave));
    });
  }
}
